import type { OfferStatus, TenancyCriteria } from "@/types/PropertyTypes";
import type { UserCommunicationMethod } from "@/types/UserTypes";
import { ApiBase } from "./ApiBase";

export interface RenterProfile {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  whatsappNumber: string;
  email: string;
  preferredCommunicationMethods: UserCommunicationMethod[];
  isVerified: boolean;
  emiratesId?: string;
  nationality?: string;
}

export interface GetRenterProfileResponse extends RenterProfile {
  preferences: TenancyCriteria | null;
  passportBlobName: string | null;
  emiratesIDFrontBlobName: string | null;
  emiratesIDBackBlobName: string | null;
  activeOffer: RenterActiveOffer | null;
  isBankAccountConnected: boolean;
  isFinancialScoreAvailable: boolean;
  isEligibleForBrackets: boolean;
}

export interface RenterActiveOffer {
  id: string;
  status: OfferStatus;
  isEscrowEnabled: boolean;
}

export interface GetPersonalDocumentsResponse {
  passportSignedUrl: string | null;
  emiratesIDFrontSignedUrl: string | null;
  emiratesIDBackSignedUrl: string | null;
}

export interface UpdateRenterProfileRequest {
  whatsappNumber: string;
  preferredCommunicationMethods: UserCommunicationMethod[];
  emiratesId?: string;
  nationality?: string;
}

export { countriesApi } from "./CountriesApi";

export class NoRenterProfileError extends Error {
  constructor() {
    super("No renter profile found.");
  }
}

export enum RenterApiErrorCode {
  NoRenterProfile = "NoRenterProfile",
}

class RenterApi extends ApiBase {
  getProfile(): Promise<GetRenterProfileResponse> {
    return this.get<GetRenterProfileResponse>("/renter/profile");
  }

  updateProfile(profile: UpdateRenterProfileRequest): Promise<void> {
    return this.post<UpdateRenterProfileRequest, void>(
      "/renter/profile",
      profile,
      {
        domainErrors: {
          [RenterApiErrorCode.NoRenterProfile]: NoRenterProfileError,
        },
      },
    );
  }

  updatePreferences(preferences: TenancyCriteria): Promise<void> {
    return this.post<TenancyCriteria, void>("/renter/preferences", preferences);
  }

  updateRenterDocuments(payload: {
    offerId: string | null;
    passportBlobName: string | null;
    emiratesIDFrontBlobName: string | null;
    emiratesIDBackBlobName: string | null;
  }): Promise<void> {
    return this.put<typeof payload, void>("/renter/documents", payload);
  }

  getPersonalDocuments(): Promise<GetPersonalDocumentsResponse> {
    return this.get<GetPersonalDocumentsResponse>("/renter/documents");
  }

  updateSingleDocument(
    documentType: "passport" | "emiratesIDFront" | "emiratesIDBack",
    blobName: string,
  ): Promise<void> {
    return this.put<{ blobName: string }, void>(
      `/renter/document/${documentType}`,
      { blobName },
    );
  }

  toggleTrust(offerId: string, isTrustEnabled: boolean): Promise<void> {
    return this.post<{ offerId: string; isTrustEnabled: boolean }, void>(
      "/renter/trust",
      {
        offerId,
        isTrustEnabled,
      },
    );
  }

  /**
   * Syncs the renter profile in the Core database with data from identity claims.
   * Called after UAE Pass linking to update verified information (FirstName, LastName, EmiratesId, etc.).
   */
  syncProfileFromIdentity(): Promise<void> {
    return this.post<Record<string, never>, void>("/renter/profile/sync", {});
  }
}

export const renterApi = new RenterApi();
